import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowUpRight, Trash2, Sparkles } from "lucide-react";

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", must_have: "", nice_to_have: "", location: "", salary_range: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [j, p] = await Promise.all([api.get("/jobs"), api.get("/professions")]);
    setJobs(j.data);
    setProfessions(p.data.professions || []);
  };
  useEffect(() => { load(); }, []);

  // Group professions by category for the dropdown
  const groupedProfessions = useMemo(() => {
    const g = {};
    professions.forEach((p) => {
      if (!g[p.category]) g[p.category] = [];
      g[p.category].push(p);
    });
    return g;
  }, [professions]);

  const applyProfession = (title) => {
    const prof = professions.find((p) => p.title === title);
    if (!prof) return;
    setForm({
      ...form,
      title: prof.title,
      description: `Poste de ${prof.title.toLowerCase()}. Exigences requises : ${prof.must_have.join(", ")}.`,
      must_have: prof.must_have.join("\n"),
      nice_to_have: (prof.nice_to_have || []).join("\n"),
    });
    toast.success(`Poste "${prof.title}" pré-rempli avec exigences officielles et nice-to-have IA`);
  };

  const createJob = async () => {
    setLoading(true);
    try {
      await api.post("/jobs", {
        ...form,
        must_have: form.must_have.split("\n").map((s) => s.trim()).filter(Boolean),
        nice_to_have: form.nice_to_have.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Poste créé");
      setOpen(false);
      setForm({ title: "", description: "", must_have: "", nice_to_have: "", location: "", salary_range: "" });
      load();
    } catch (e) {
      toast.error("Erreur création");
    } finally { setLoading(false); }
  };

  const removeJob = async (id) => {
    if (!confirm("Supprimer ce poste et tous ses candidats ?")) return;
    await api.delete(`/jobs/${id}`);
    toast.success("Poste supprimé");
    load();
  };

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-slate-500 mb-2">Postes</div>
          <h1 className="font-display text-4xl tracking-tight">Vos recrutements en cours</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F172A] hover:bg-[#1E293B] btn-pill px-6" data-testid="jobs-new-btn">+ Nouveau poste</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>Nouveau poste</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 text-[#2563EB]" /> Métier standard (optionnel · auto-remplit les champs)
                </Label>
                <Select onValueChange={applyProfession}>
                  <SelectTrigger className="mt-1.5" data-testid="job-form-profession">
                    <SelectValue placeholder="Choisir un métier de la bibliothèque…" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {Object.entries(groupedProfessions).map(([cat, list]) => (
                      <SelectGroup key={cat}>
                        <SelectLabel className="text-[10px] tracking-widest uppercase text-slate-500">{cat}</SelectLabel>
                        {list.map((p) => <SelectItem key={p.title} value={p.title}>{p.title}</SelectItem>)}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-xs text-slate-500 mt-1.5">
                  Sélectionner un métier remplit automatiquement titre, description et exigences officielles (diplômes, permis, certifications).
                </div>
              </div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Intitulé *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" placeholder="ex. Senior Backend Engineer" data-testid="job-form-title" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Description *</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 min-h-[100px]" placeholder="Mission, contexte, stack…" data-testid="job-form-description" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-500">Must-have (1 par ligne)</Label>
                <Textarea value={form.must_have} onChange={(e) => setForm({ ...form, must_have: e.target.value })} className="mt-1.5 font-mono text-sm" placeholder="5+ ans Python&#10;Expérience SaaS B2B" data-testid="job-form-musthave" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                Nice-to-have <Sparkles className="w-3 h-3 text-purple-600" /> <span className="text-slate-400 lowercase font-normal">(optionnel · rempli par l'IA)</span>
              </Label>
                <Textarea value={form.nice_to_have} onChange={(e) => setForm({ ...form, nice_to_have: e.target.value })} className="mt-1.5 font-mono text-sm" placeholder="Laissez vide, l'IA les déduira automatiquement du métier choisi" data-testid="job-form-nicetohave" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Lieu</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1.5" data-testid="job-form-location" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-slate-500">Salaire</Label>
                  <Input value={form.salary_range} onChange={(e) => setForm({ ...form, salary_range: e.target.value })} className="mt-1.5" data-testid="job-form-salary" /></div>
              </div>
              <Button onClick={createJob} disabled={!form.title || !form.description || loading} className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill h-11" data-testid="job-form-submit">
                {loading ? "Création…" : "Créer le poste"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {jobs.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-16 text-center bg-white">
          <p className="text-slate-500 mb-4">Aucun poste ouvert.</p>
          <Button onClick={() => setOpen(true)} className="bg-[#2563EB] hover:bg-[#1D4ED8] btn-pill" data-testid="jobs-empty-create">Créer mon premier poste</Button>
        </div>
      ) : (
        <div className="border border-slate-200 bg-white">
          {jobs.map((j) => (
            <div key={j.id} className="flex items-center justify-between p-6 border-b last:border-b-0 border-slate-100 hover:bg-slate-50 group">
              <Link to={`/app/jobs/${j.id}`} className="flex-1" data-testid={`job-row-${j.id}`}>
                <div className="font-medium text-lg">{j.title}</div>
                <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-3 font-mono">
                  <span>{j.candidate_count} candidat(s)</span>
                  <span>·</span>
                  <span>{j.must_have?.length || 0} must-have</span>
                  {j.location && <><span>·</span><span>{j.location}</span></>}
                </div>
              </Link>
              <div className="flex items-center gap-2">
                <button onClick={() => removeJob(j.id)} className="p-2 text-slate-400 hover:text-red-600 transition-colors" data-testid={`job-delete-${j.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
                <Link to={`/app/jobs/${j.id}`} className="p-2"><ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-slate-900" /></Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
